import React from 'react';

/**
 * work_items_109 — Investment Indicators monetary-unit wrapping.
 *
 * Value cells such as `1,488,322千ドル` must keep the number and the unit on the
 * same line while space allows, and only break *before* the complete unit when
 * the cell is too narrow. The unit itself must never split internally
 * (`千ド` / `ル` is forbidden), so this renderer emits a discretionary `<wbr>`
 * break opportunity plus an atomic unit span.
 *
 * Presentation-only: mappers keep returning plain display strings.
 */

/**
 * Recognised monetary units, longest-first.
 *
 * Longest-first matching is mandatory so `円` cannot capture the tail of
 * `千円`/`百万円` and `ドル` cannot capture the tail of `千ドル`/`百万ドル`.
 * `%`, `倍`, `回`, and `株` are intentionally excluded.
 */
export const MONETARY_UNITS = [
  '百万ドル',
  '百万円',
  '千ドル',
  '千円',
  'ドル',
  '円',
] as const;

export interface MonetaryValueParts {
  /** Text before the unit, with trailing whitespace/newlines removed. */
  prefix: string;
  /** The complete, indivisible monetary unit token. */
  unit: string;
  /** Text after the unit (e.g. a stock-price timestamp), unchanged. */
  suffix: string;
}

/**
 * Splits a display value at its first recognised monetary unit.
 *
 * Returns `null` when the value is empty, is `--`, or contains no recognised
 * monetary unit; callers then render the original string unchanged.
 */
export const splitMonetaryValue = (
  value: string | undefined | null,
): MonetaryValueParts | null => {
  if (typeof value !== 'string' || value.length === 0) return null;

  for (let index = 0; index < value.length; index += 1) {
    // Scan left-to-right, trying the longest unit first at each position so the
    // earliest occurrence wins and compound units stay whole.
    const unit = MONETARY_UNITS.find((candidate) => value.startsWith(candidate, index));
    if (!unit) continue;

    // Historical static fixtures embed "\n" right before the unit; drop that
    // whitespace so `pre-line` cannot force a permanent line break.
    const prefix = value.slice(0, index).replace(/\s+$/, '');
    if (prefix.length === 0) return null;

    return {
      prefix,
      unit,
      suffix: value.slice(index + unit.length),
    };
  }

  return null;
};

export interface InvestmentInfoValueProps {
  /** Mapped display value for a simple row or grouped child. */
  value?: string;
}

const InvestmentInfoValue: React.FC<InvestmentInfoValueProps> = ({ value }) => {
  const text = value ?? '';
  const parts = splitMonetaryValue(text);

  if (!parts) return <>{text}</>;

  return (
    <>
      {parts.prefix}
      <wbr />
      <span className="investment-info-table__monetary-unit">{parts.unit}</span>
      {parts.suffix}
    </>
  );
};

export default InvestmentInfoValue;
